// Right pane — focused-section reading model (source-map restructure MR 5b).
//
// The pre-existing implementation rendered the entire chapter as one long
// scroll with every sub-section highlighted via colored spans + inline
// action clusters + gap overlays. MR 5b replaces that with the designer's
// focused-section idiom: ONLY the current sub-section renders in full;
// preceding + following sub-sections collapse to a mini-header + a
// "... preceding section ..." / "... following section ..." fade marker.
// Navigation between sections is explicit — a click on the left pane row
// (or J/K in MR 11) advances current. No scroll-spy; no whole-chapter
// scroll to lose your place inside.
//
// What this deletes vs prior MR 5:
//   - buildRuns / getRunClass / SECTION_PALETTE — all whole-chapter
//     rendering is gone; runs are no longer computed
//   - Scroll-spy effect + onCurrentSectionChange dispatch — current is
//     explicit, not derived from viewport midpoint
//   - Gap rendering + gap action cluster — gaps aren't visualised in
//     the focused-section model (a gap is a data problem for the
//     reviewer to fix at the boundary; there is no "unattributed source
//     text" scroll to see it in)
//   - NoMatchStrip / inline red boundary strips — a no-match sub still
//     renders its header at the top of its body slot; the body slot is
//     just empty
//   - Section-first-run action trigger — the row's action cluster on
//     the LEFT is the section-scope entry point now
//
// What this preserves:
//   - Selection detection (mouseup → range → toolbar) — MR 7 rewires
//     the toolbar
//   - onSelectionAction callback signature — MR 7 rewires
//   - Click-away handling for the (future) selection toolbar
//   - Chapter no-match banner

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { ResolvedChapter, MapGap } from '@gc-coach/shared';
import { classifyChunkSize } from '@gc-coach/shared';
import type { MapTreeFocus } from './MapTree.js';
import { ChunkSizeBadge } from './ChunkSizeBadge.js';
import { ParagraphEditor } from './ParagraphEditor.js';
import styles from './SourceDocPane.module.css';

/** Kept for wire compat with MapReviewPage's existing callback plumbing.
 *  MR 5b's SourceDocPane no longer invokes these; MR 8 (section-action
 *  row cluster) will re-wire. */
export type GapAction =
  | 'new-section-from-gap'
  | 'extend-previous-section'
  | 'mark-intentional-gap';
export type SectionAction = 'toggle-excluded' | 'delete-section';

/** Actions triggered from the selection toolbar + inline paragraph
 *  editor. All refer to paragraph indices within the CURRENT sub-section,
 *  computed from the DOM's data-para-idx attribute against the browser
 *  Selection endpoints.
 *
 *  Boundary actions (MR 7): the data model expresses sub-sections via
 *  header_pattern matches, so Split + Move land as inserting a new sub /
 *  adjusting a neighbour's pattern — no per-paragraph state needed.
 *
 *  Paragraph-content actions (MR 8, enabled by MR 7b's data model):
 *  rewrite / insert-after / exclude write entries into the current sub's
 *  `paragraph_overrides` array; chunker honours them at ingest. */
export type SelectionAction =
  /** Section-tier split (2026-10-26 redesign: caret + preview) — a cut
   *  placed between two words carves the current section into prefix +
   *  new, with an optional second cut (carve) adding a suffix that
   *  resumes the parent.
   *  `startSeed` is the raw text slice beginning at the first cut
   *  (used to derive the new section's header_pattern). Null when the
   *  cut sits at the current section's origin → no prefix, no first
   *  pattern needed.
   *  `endSeed` is the equivalent for the carve cut; null in the common
   *  single-cut case or when the second cut sits at the section's tail.
   *  Both null → no-op, parent returns null.
   *  `startLabel` / `endLabel` carry the editable titles the reviewer
   *  typed in the sticky result card; empty strings leave the inserted
   *  sub with no label (server resolves the header on save). */
  | {
      kind: 'split-range';
      startSeed: string | null;
      endSeed: string | null;
      startLabel?: string;
      endLabel?: string;
    }
  | {
      kind: 'move-to-prev' | 'move-to-next';
      startParaIdx: number;
      endParaIdx: number;
    }
  | {
      kind: 'rewrite-paragraph';
      paragraphIdx: number;
      originalText: string;
      replacementText: string;
    }
  | {
      kind: 'insert-after-paragraph';
      paragraphIdx: number;
      insertedText: string;
    }
  | { kind: 'exclude-paragraph'; paragraphIdx: number };

/** Section-tier split mode state (2026-10-26 redesign). See the detailed
 *  comment inside SourceDocPane. Hoisted to module scope so the helper
 *  components can reference it. */
export type SplitState =
  | null
  | { stage: 'armed' }
  | { stage: 'oneCut'; a: number; titleA: string; carveArm: boolean }
  | { stage: 'twoCuts'; a: number; b: number; titleA: string; titleB: string };

export interface SourceDocPaneProps {
  sourceText: string;
  chapter: ResolvedChapter;
  /** Last-saved shape of the same chapter — powers the was-tokens
   *  delta chip on ChunkSizeBadge (MR 9). Optional; unset renders
   *  badges without delta chips. */
  baselineChapter?: ResolvedChapter;
  /** 1-indexed chapter number for the sticky reading header + `§n.m`
   *  refs on section markers. */
  chapterNumber: number;
  /** MR 5b: unused (gap visualisation removed). Prop kept so the parent
   *  doesn't have to refactor. */
  gaps: readonly MapGap[];
  /** Which sub-section the reviewer is currently reading. Number = the
   *  matching sub. 'chapter' = default to the first sub. null = same. */
  focused: MapTreeFocus | null;
  /** True when the map is approved — hides text-selection affordances. */
  readOnly: boolean;
  /** MR 5b: unused. Preserved so MapReviewPage's onGapAction stays wired. */
  onGapAction: (gapKey: string, action: GapAction) => void;
  /** Delete / toggle-excluded on a sub-section. Called from the new
   *  SectionFloatingMenu (right pane). Also still called from the
   *  left-pane row for toggle-excluded (unchanged). */
  onSectionAction: (subIdx: number, action: SectionAction) => void;
  /** Merge the given sub-section into its predecessor. Called from the
   *  SectionFloatingMenu. Same handler as the left pane used in MR 8;
   *  we route both triggers through MapReviewPage. */
  onSectionMergeUp: (subIdx: number) => void;
  /** Selection-toolbar actions. Fired when the reviewer picks Move /
   *  Rewrite / Insert / Exclude off the sticky toolbar that appears when
   *  text is selected inside the current section. Split is no longer in
   *  this toolbar — it's a section-tier action triggered from the
   *  SectionFloatingMenu (click → draggable split bar). */
  onSelectionAction: (action: SelectionAction) => void;
  t: (k: string) => string;
}

export function SourceDocPane({
  sourceText,
  chapter,
  baselineChapter,
  chapterNumber,
  focused,
  readOnly,
  onSectionAction,
  onSectionMergeUp,
  onSelectionAction,
  t,
}: SourceDocPaneProps) {
  const paneRef = useRef<HTMLDivElement | null>(null);
  const docBodyRef = useRef<HTMLDivElement | null>(null);

  // Text-selection state (MR 7). Tracks paragraph indices within the
  // current sub-section — the SelectionToolbar reads this to render the
  // Split / Move affordances, and the split preview line uses
  // `startParaIdx` to decide where to draw itself.
  const [selection, setSelection] = useState<{
    startParaIdx: number;
    endParaIdx: number;
    wordCount: number;
  } | null>(null);

  // MR 8 — inline paragraph editor state. When the reviewer picks
  // Rewrite / Insert after from the toolbar, we remember which
  // paragraph + mode they picked; the paragraph renderer swaps in a
  // <ParagraphEditor /> at that slot. One editor at a time — opening a
  // second cancels the first (same pattern as inline cell editors in
  // the KB list).
  const [editor, setEditor] = useState<
    | { mode: 'rewrite'; paragraphIdx: number; initialText: string }
    | { mode: 'insert'; paragraphIdx: number }
    | null
  >(null);

  // Section-tier Split mode — 2026-10-26 redesign (designer handoff
  // `for-engineering-split-new`): the section enters an "armed" state;
  // a blinking caret follows the mouse snapping between words; a click
  // places a cut; a sticky result card at the top shows the resulting
  // halves with an editable title and the derived anchor phrase. The
  // carve case (3 sections) is a second cut placed below the first.
  // State shape lives at module scope (see `SplitState` above).
  const [splitState, setSplitState] = useState<SplitState>(null);
  const [splitHover, setSplitHover] = useState<number | null>(null);
  const [splitDrag, setSplitDrag] = useState<'a' | 'b' | null>(null);

  const chapterStart = chapter.char_start;
  const chapterEnd = chapter.char_end;

  useEffect(() => {
    setSelection(null);
    setEditor(null);
    setSplitState(null);
    setSplitHover(null);
    setSplitDrag(null);
  }, [chapterStart, chapterEnd]);

  // Reset per-section modes (split, editor) when the current sub
  // changes — otherwise opening Split on §1.4 then clicking into §1.5
  // would leave the caret stranded on the wrong section's text.
  useEffect(() => {
    setSplitState(null);
    setSplitHover(null);
    setSplitDrag(null);
    setEditor(null);
  }, [focused]);

  // Selection detection — uses document `selectionchange` (fires for
  // both drag-select and caret positioning) instead of `mouseup` so
  // keyboard-driven selection works too. We track only NON-COLLAPSED
  // selections in MR 7 (a range picks the target paragraph/s
  // unambiguously; caret-only Split lands later once we decide on its
  // trigger).
  useEffect(() => {
    if (readOnly) {
      setSelection(null);
      return;
    }
    const onSelectionChange = () => {
      const body = docBodyRef.current;
      if (!body) return;
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
        setSelection(null);
        return;
      }
      const range = sel.getRangeAt(0);
      if (
        !body.contains(range.startContainer) ||
        !body.contains(range.endContainer)
      ) {
        setSelection(null);
        return;
      }
      const startIdx = findParaIdx(range.startContainer);
      const endIdx = findParaIdx(range.endContainer);
      if (startIdx === null || endIdx === null) {
        setSelection(null);
        return;
      }
      const from = Math.min(startIdx, endIdx);
      const to = Math.max(startIdx, endIdx);
      // Word count on the raw selected text — cheap heuristic; matches
      // the mock's `1 PARAGRAPH · N WORDS` chip.
      const words = sel.toString().trim().split(/\s+/).filter(Boolean).length;
      setSelection({ startParaIdx: from, endParaIdx: to, wordCount: words });
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [readOnly]);

  // Esc / Enter global handler for split mode is set up BELOW, after
  // `wordIndex` is derived, so the commit callback can reference it.
  const splitCommitRef = useRef<() => void>(() => {});

  // Resolve the current sub-section from `focused`. Falls back to the
  // first sub when nothing is explicitly focused OR when focused points
  // at the chapter itself (the mock treats "chapter" as "top of the
  // reading" — same behavior as first sub for the reading pane).
  //
  // All derivations MUST be computed unconditionally — the early return
  // for the "chapter didn't resolve" case sits AFTER this block so
  // React's hook-order invariant is preserved regardless of whether the
  // chapter resolved. Values are inert (empty arrays / null) when the
  // chapter has no offsets and simply aren't rendered.
  const subCount = chapter.sub_sections.length;
  const explicit =
    focused && typeof focused.target === 'number' ? focused.target : 0;
  const currentIdx = subCount === 0 ? -1 : Math.max(0, Math.min(subCount - 1, explicit));
  const currentSub = currentIdx >= 0 ? chapter.sub_sections[currentIdx] ?? null : null;
  const prevSub = currentIdx > 0 ? chapter.sub_sections[currentIdx - 1] ?? null : null;
  const nextSub =
    currentIdx >= 0 && currentIdx < subCount - 1
      ? chapter.sub_sections[currentIdx + 1] ?? null
      : null;

  // Body text for the current sub, split into paragraphs. A `\n{2,}`
  // split matches the chunker's own paragraph split (see
  // chunker.ts::splitIfOversized) so what the reviewer reads here is
  // exactly what the chunker will split on if the section is oversized.
  //
  // Memoised on the sub's resolved offsets so the identity survives
  // between renders — the downstream useMemos (paragraphTokens,
  // cumulativeTokens) get a stable dependency and only recompute when
  // the underlying section text actually changes.
  const paragraphs = useMemo(() => {
    if (!currentSub || currentSub.char_start === null || currentSub.char_end === null) {
      return [] as string[];
    }
    const body = sourceText.slice(currentSub.char_start, currentSub.char_end).trim();
    if (!body) return [] as string[];
    return body
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
  }, [sourceText, currentSub]);

  // Word-indexed view of the current sub-section body — the split
  // redesign (2026-10-26) places cuts between words. `words` is a flat
  // array of tokens; `wordParaStart[i]` is the paragraph index that
  // word `i` belongs to (so the renderer can wrap each paragraph's
  // slice back in a `<p>`); `wordParaOffsetChar[i]` is the character
  // offset within the paragraph at which word `i` begins (used to
  // produce `startSeed`: everything from the cut onward within THIS
  // paragraph, extended into the next paragraph if the tail is too
  // short to form a reliable header_pattern).
  const wordIndex = useMemo(() => {
    const w: string[] = [];
    const paraOfWord: number[] = [];
    const charOffsetOfWord: number[] = [];
    paragraphs.forEach((p, pi) => {
      // Match the paragraph's own split (whitespace runs). We track
      // char offsets so we can reconstruct a seed slice from the
      // paragraph body without re-tokenising.
      const re = /\S+/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(p)) !== null) {
        w.push(m[0]);
        paraOfWord.push(pi);
        charOffsetOfWord.push(m.index);
      }
    });
    return { words: w, paraOfWord, charOffsetOfWord };
  }, [paragraphs]);

  // Full chapter body text used for the pattern-uniqueness check in
  // the sticky result card. The server chunker is the authoritative
  // pattern-matcher; this is a live preview so the reviewer sees
  // "EXTENDED TO N WORDS" or "✓ UNIQUE" the moment they place the cut.
  const chapterBodyText = useMemo(() => {
    if (chapterStart === null || chapterEnd === null) return '';
    return sourceText.slice(chapterStart, chapterEnd);
  }, [sourceText, chapterStart, chapterEnd]);

  // MR 8 — split the current sub's overrides into fast lookup maps so
  // the paragraph renderer can apply tints without walking the array N
  // times. Same bucketing shape the chunker uses (see
  // applyParagraphOverrides in @gc-coach/api).
  const overrideBuckets = useMemo(() => {
    const buckets = {
      exclude: new Set<number>(),
      rewrite: new Map<number, { replacement_text: string; original_text: string }>(),
      insertAfter: new Map<number, string[]>(),
    };
    for (const o of currentSub?.paragraph_overrides ?? []) {
      if (o.kind === 'exclude') {
        buckets.exclude.add(o.paragraph_index);
      } else if (o.kind === 'rewrite') {
        buckets.rewrite.set(o.paragraph_index, {
          replacement_text: o.replacement_text,
          original_text: o.original_text,
        });
      } else if (o.kind === 'insert_after') {
        const list = buckets.insertAfter.get(o.paragraph_index) ?? [];
        list.push(o.inserted_text);
        buckets.insertAfter.set(o.paragraph_index, list);
      }
    }
    return buckets;
  }, [currentSub?.paragraph_overrides]);

  // Commit callback — fires on Enter (global handler below) or on the
  // sticky card's Create button. Derives per-cut seeds from the word
  // index + paragraph text; the server-side chunker trims these to
  // authoritative header_patterns at save.
  const commitSplit = useCallback(() => {
    if (splitState === null || splitState.stage === 'armed') return;
    // Use `derivePattern` client-side so the seed we hand the reducer
    // is already a chapter-unique prefix. The chunker's own
    // `deriveHeaderPatternFromText` is a plain word-prefix truncation
    // with no uniqueness check — if we feed it a raw paragraph slice
    // whose first words recur elsewhere in the chapter (common when
    // the end cut is near the end of §X), its `findLastMatch` picks a
    // later occurrence and the RESUMES sub-section fails to anchor
    // (visible as the "NO MATCH" badge).
    const uniqueSeedAt = (from: number): string | null => {
      const pat = derivePattern(from, wordIndex.words, chapterBodyText);
      if (!pat || pat.fail) return null;
      return pat.text;
    };
    const startSeed = uniqueSeedAt(splitState.a);
    if (!startSeed) {
      // Can't anchor the start cut — refuse rather than ship a broken
      // split. The sticky card already surfaces this as a warning
      // (patA.fail flag) but we guard the commit path too.
      return;
    }
    if (splitState.stage === 'oneCut') {
      onSelectionAction({
        kind: 'split-range',
        startSeed,
        endSeed: null,
        ...(splitState.titleA.trim() ? { startLabel: splitState.titleA.trim() } : {}),
      });
    } else {
      // Carve. If the end cut can't be made chapter-unique, drop it
      // and commit as a plain 2-section split instead of writing a
      // RESUMES sub-section the server can't resolve.
      const endSeed = uniqueSeedAt(splitState.b);
      if (!endSeed) {
        onSelectionAction({
          kind: 'split-range',
          startSeed,
          endSeed: null,
          ...(splitState.titleA.trim() ? { startLabel: splitState.titleA.trim() } : {}),
        });
      } else {
        onSelectionAction({
          kind: 'split-range',
          startSeed,
          endSeed,
          ...(splitState.titleA.trim() ? { startLabel: splitState.titleA.trim() } : {}),
          ...(splitState.titleB.trim() ? { endLabel: splitState.titleB.trim() } : {}),
        });
      }
    }
    setSplitState(null);
    setSplitHover(null);
    setSplitDrag(null);
  }, [splitState, wordIndex, chapterBodyText, onSelectionAction]);
  splitCommitRef.current = commitSplit;

  // Esc cancels split mode globally (the sticky result card also has an
  // explicit Cancel · Esc button; clicking outside does NOT cancel once
  // a cut is placed — the brief says "a stray click can't throw away a
  // careful placement"). Enter commits once a cut is placed and focus
  // isn't on an input (the title field).
  useEffect(() => {
    if (splitState === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (splitState.stage === 'oneCut' && splitState.carveArm) {
          setSplitState({ ...splitState, carveArm: false });
        } else {
          setSplitState(null);
          setSplitHover(null);
          setSplitDrag(null);
        }
      } else if (e.key === 'Enter' && splitState.stage !== 'armed') {
        const active = document.activeElement;
        if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
          return;
        }
        e.preventDefault();
        splitCommitRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [splitState]);

  // MR 9 — last-saved token counts indexed by sub position (see
  // detailed comment on the derived reading-was-tokens value below).
  // Hoisted above the early return so hook order stays stable across
  // the resolved/unresolved boundary.
  const wasTokensBySubIdx = useMemo(() => {
    const m = new Map<number, number>();
    if (!baselineChapter) return m;
    baselineChapter.sub_sections.forEach((s, i) => {
      if (typeof s.estimated_tokens === 'number') m.set(i, s.estimated_tokens);
    });
    return m;
  }, [baselineChapter]);

  // Chapter didn't resolve — banner. AFTER the derivations above so
  // hook order stays stable across the resolved/unresolved boundary.
  if (chapterStart === null || chapterEnd === null) {
    return (
      <div className={styles.pane} ref={paneRef}>
        <div className={styles.noMatchBanner}>
          <span className={styles.noMatchBannerIcon}>⚠</span>
          <div>
            <strong>{t('mapReview.docPaneChapterNoMatchTitle')}</strong>
            <p>{t('mapReview.docPaneChapterNoMatchBody')}</p>
          </div>
        </div>
      </div>
    );
  }

  const readingLabel = currentSub ? currentSub.label : chapter.title;
  const readingTokens = currentSub
    ? currentSub.estimated_tokens ?? null
    : chapter.estimated_tokens ?? null;
  // Was-tokens for the reading header — the reader is either on a sub
  // (index lookup) or on the chapter header (chapter-level baseline).
  // Powers ChunkSizeBadge's `↓ was ~N` delta chip; comparing by INDEX
  // matches what the reviewer sees on the left row.
  const readingWasTokens = currentSub
    ? wasTokensBySubIdx.get(currentIdx)
    : baselineChapter?.estimated_tokens;

  return (
    <div className={styles.pane} ref={paneRef}>
      {/* Sticky reading header — labels the current section + shows
        * its live token badge. Position:sticky at top of the pane so
        * it stays visible while the reader scrolls the current
        * section's paragraphs. */}
      <div className={styles.readingHeader}>
        <span className={styles.readingLabel}>{t('mapReview.readingLabel')}</span>
        <span className={styles.readingTitle}>{readingLabel || ''}</span>
        {readingTokens != null && (
          <ChunkSizeBadge
            estimatedTokens={readingTokens}
            isChunk={currentSub != null}
            {...(readingWasTokens !== undefined
              ? { wasEstimatedTokens: readingWasTokens }
              : {})}
            t={t}
          />
        )}
        <span className={styles.readingHint}>{t('mapReview.readingHint')}</span>
      </div>

      {selection && !readOnly && currentSub && !editor && (
        <SelectionToolbar
          selection={selection}
          canMovePrev={prevSub != null}
          canMoveNext={nextSub != null}
          prevRef={
            prevSub ? `§${chapterNumber}.${currentIdx}` : null
          }
          nextRef={
            nextSub ? `§${chapterNumber}.${currentIdx + 2}` : null
          }
          onAction={onSelectionAction}
          onOpenRewrite={(paragraphIdx) => {
            const initial = paragraphs[paragraphIdx] ?? '';
            setEditor({ mode: 'rewrite', paragraphIdx, initialText: initial });
            window.getSelection()?.removeAllRanges();
          }}
          onOpenInsert={(paragraphIdx) => {
            setEditor({ mode: 'insert', paragraphIdx });
            window.getSelection()?.removeAllRanges();
          }}
          t={t}
        />
      )}

      <div className={styles.doc} ref={docBodyRef}>
        {prevSub && (
          <SectionMarker
            chapterNumber={chapterNumber}
            subIdx={currentIdx - 1}
            sub={prevSub}
            {...(wasTokensBySubIdx.get(currentIdx - 1) !== undefined
              ? { wasEstimatedTokens: wasTokensBySubIdx.get(currentIdx - 1)! }
              : {})}
            direction="preceding"
            dimmed={splitState !== null}
            t={t}
          />
        )}
        {prevSub && currentSub && !readOnly && (
          <BoundaryDrag
            edge="top"
            bodyRef={docBodyRef}
            paragraphCount={paragraphs.length}
            onCommit={(snapIdx) => {
              // Dragging TOP boundary DOWN moves paragraphs [0, snapIdx-1]
              // UP into the previous section. snapIdx === 0 is a no-op.
              if (snapIdx <= 0) return;
              onSelectionAction({
                kind: 'move-to-prev',
                startParaIdx: 0,
                endParaIdx: snapIdx - 1,
              });
            }}
            t={t}
          />
        )}
        {prevSub && currentSub && readOnly && (
          <div className={styles.bnd} aria-hidden="true" />
        )}

        {currentSub && (
          <div
            className={joinSecClasses(
              styles.sec,
              styles.secCurrent,
              tierFillClass(styles, currentSub.estimated_tokens),
              splitState !== null && styles.secArmed,
            )}
          >
            <div className={styles.sech}>
              <span className={styles.sechRef}>
                §{chapterNumber}.{currentIdx + 1}
              </span>
              <span className={styles.sechDot} aria-hidden="true">·</span>
              <span className={styles.sechTitle}>{currentSub.label}</span>
              {currentSub.estimated_tokens != null && (
                <ChunkSizeBadge
                  estimatedTokens={currentSub.estimated_tokens}
                  isChunk={true}
                  {...(wasTokensBySubIdx.get(currentIdx) !== undefined
                    ? { wasEstimatedTokens: wasTokensBySubIdx.get(currentIdx)! }
                    : {})}
                  t={t}
                />
              )}
              {/* Section-tier floating menu — Split / Merge up / Delete.
                * Hidden while split mode is active (the sticky result
                * card + banner carry their own Cancel · Esc control;
                * showing the toggled Split button at the same time
                * reads as noise per the designer handoff). */}
              {!readOnly && splitState === null && (
                <SectionFloatingMenu
                  canMergeUp={prevSub != null}
                  onSplit={() => setSplitState({ stage: 'armed' })}
                  onMergeUp={() => onSectionMergeUp(currentIdx)}
                  onDelete={() => onSectionAction(currentIdx, 'delete-section')}
                  t={t}
                />
              )}
            </div>
            {splitState?.stage === 'armed' && (
              <SplitBanner t={t} onCancel={() => setSplitState(null)} />
            )}
            {splitState !== null && splitState.stage !== 'armed' && (
              <SplitResultCard
                state={splitState}
                words={wordIndex.words}
                chapterBodyText={chapterBodyText}
                parentRef={`§${chapterNumber}.${currentIdx + 1}`}
                onChangeTitleA={(titleA) => {
                  if (splitState.stage === 'oneCut') {
                    setSplitState({ ...splitState, titleA });
                  } else if (splitState.stage === 'twoCuts') {
                    setSplitState({ ...splitState, titleA });
                  }
                }}
                onChangeTitleB={(titleB) => {
                  if (splitState.stage === 'twoCuts') {
                    setSplitState({ ...splitState, titleB });
                  }
                }}
                onArmCarve={() => {
                  if (splitState.stage === 'oneCut') {
                    setSplitState({ ...splitState, carveArm: true });
                  }
                }}
                onRemoveEnd={() => {
                  if (splitState.stage === 'twoCuts') {
                    setSplitState({
                      stage: 'oneCut',
                      a: splitState.a,
                      titleA: splitState.titleA,
                      carveArm: false,
                    });
                  }
                }}
                onCommit={() => splitCommitRef.current()}
                onCancel={() => {
                  setSplitState(null);
                  setSplitHover(null);
                  setSplitDrag(null);
                }}
                t={t}
              />
            )}
            {paragraphs.length === 0 ? (
              <p className={styles.secEmptyBody}>
                {currentSub.char_start === null
                  ? t('mapReview.docPaneSubNoMatchBody')
                  : t('mapReview.docPaneSubEmptyBody')}
              </p>
            ) : splitState !== null ? (
              <SplitBody
                paragraphs={paragraphs}
                wordIndex={wordIndex}
                state={splitState}
                hover={splitHover}
                drag={splitDrag}
                chapterBodyText={chapterBodyText}
                onHoverChange={setSplitHover}
                onClickBoundary={(boundary) => {
                  if (splitState.stage === 'armed') {
                    setSplitState({
                      stage: 'oneCut',
                      a: boundary,
                      titleA: '',
                      carveArm: false,
                    });
                    setSplitHover(null);
                  } else if (
                    splitState.stage === 'oneCut' &&
                    splitState.carveArm &&
                    // Enforce the mock's "at least 3 words apart" guard
                    // so the carve range is visually non-empty.
                    boundary > splitState.a + 3
                  ) {
                    setSplitState({
                      stage: 'twoCuts',
                      a: splitState.a,
                      b: boundary,
                      titleA: splitState.titleA,
                      titleB: '',
                    });
                    setSplitHover(null);
                  }
                }}
                onDragStart={(which) => setSplitDrag(which)}
                onDragMove={(which, boundary) => {
                  if (which === 'a') {
                    if (splitState.stage === 'oneCut') {
                      setSplitState({ ...splitState, a: boundary });
                    } else if (
                      splitState.stage === 'twoCuts' &&
                      boundary < splitState.b - 3
                    ) {
                      setSplitState({ ...splitState, a: boundary });
                    }
                  } else if (which === 'b' && splitState.stage === 'twoCuts') {
                    if (boundary > splitState.a + 3) {
                      setSplitState({ ...splitState, b: boundary });
                    }
                  }
                }}
                onDragEnd={() => setSplitDrag(null)}
                t={t}
              />
            ) : (
              paragraphs.map((p, i) => {
                const excluded = overrideBuckets.exclude.has(i);
                const rewrite = overrideBuckets.rewrite.get(i);
                const insertsAfter = overrideBuckets.insertAfter.get(i) ?? [];
                const editingThis = editor?.paragraphIdx === i;
                return (
                  <span key={i}>
                    {/* Paragraph itself. Rewrite mode replaces it with
                      * an inline editor at the same slot; otherwise
                      * tints apply based on the override (if any). */}
                    {editingThis && editor?.mode === 'rewrite' ? (
                      <ParagraphEditor
                        mode="rewrite"
                        initialText={editor.initialText}
                        onSave={(text) => {
                          onSelectionAction({
                            kind: 'rewrite-paragraph',
                            paragraphIdx: i,
                            originalText: p,
                            replacementText: text,
                          });
                          setEditor(null);
                        }}
                        onCancel={() => setEditor(null)}
                        t={t}
                      />
                    ) : (
                      <p
                        data-para-idx={i}
                        className={paragraphClass(styles, { excluded, rewritten: !!rewrite })}
                      >
                        {rewrite ? rewrite.replacement_text : p}
                      </p>
                    )}
                    {/* Any insert_after blocks render immediately below
                      * their anchor paragraph, each tinted --accent so
                      * provenance stays visible even for a reader
                      * who scans the pane later. */}
                    {insertsAfter.map((inserted, k) => (
                      <p key={`ins-${i}-${k}`} className={styles.paraInserted}>
                        {inserted}
                      </p>
                    ))}
                    {/* Insert editor appears AFTER its anchor paragraph. */}
                    {editingThis && editor?.mode === 'insert' && (
                      <ParagraphEditor
                        mode="insert"
                        initialText=""
                        onSave={(text) => {
                          onSelectionAction({
                            kind: 'insert-after-paragraph',
                            paragraphIdx: i,
                            insertedText: text,
                          });
                          setEditor(null);
                        }}
                        onCancel={() => setEditor(null)}
                        t={t}
                      />
                    )}
                  </span>
                );
              })
            )}
          </div>
        )}

        {currentSub && nextSub && !readOnly && (
          <BoundaryDrag
            edge="bottom"
            bodyRef={docBodyRef}
            paragraphCount={paragraphs.length}
            onCommit={(snapIdx) => {
              // Dragging BOTTOM boundary UP moves paragraphs
              // [snapIdx, last] DOWN into the next section. If snapIdx
              // is the last paragraph +1 (past end), nothing to move.
              if (snapIdx >= paragraphs.length) return;
              onSelectionAction({
                kind: 'move-to-next',
                startParaIdx: snapIdx,
                endParaIdx: paragraphs.length - 1,
              });
            }}
            t={t}
          />
        )}
        {currentSub && nextSub && readOnly && (
          <div className={styles.bnd} aria-hidden="true" />
        )}
        {nextSub && (
          <SectionMarker
            chapterNumber={chapterNumber}
            subIdx={currentIdx + 1}
            sub={nextSub}
            {...(wasTokensBySubIdx.get(currentIdx + 1) !== undefined
              ? { wasEstimatedTokens: wasTokensBySubIdx.get(currentIdx + 1)! }
              : {})}
            direction="following"
            dimmed={splitState !== null}
            t={t}
          />
        )}
      </div>
    </div>
  );
}

// ─── Section marker (preceding / following) ───────────────────────────

/** A collapsed neighbour-section marker — mini header + fade text saying
 *  "... preceding section ..." / "... following section ...". The
 *  reviewer sees enough context to know what's around the current
 *  section without seeing its body. Click behavior lives on the left-
 *  pane row (this marker is display-only). */
function SectionMarker({
  chapterNumber,
  subIdx,
  sub,
  wasEstimatedTokens,
  direction,
  dimmed,
  t,
}: {
  chapterNumber: number;
  subIdx: number;
  sub: { label: string; estimated_tokens?: number };
  wasEstimatedTokens?: number;
  direction: 'preceding' | 'following';
  /** True while the current section is in split mode — dims neighbour
   *  markers so the reviewer's eye stays on the section being carved. */
  dimmed?: boolean;
  t: (k: string) => string;
}) {
  const key =
    direction === 'preceding'
      ? 'mapReview.docPanePrecedingSection'
      : 'mapReview.docPaneFollowingSection';
  return (
    <div className={joinSecClasses(styles.sec, dimmed && styles.secNeighbor)}>
      <div className={styles.sech}>
        <span className={styles.sechRef}>
          §{chapterNumber}.{subIdx + 1}
        </span>
        <span className={styles.sechDot} aria-hidden="true">·</span>
        <span className={styles.sechTitle}>{sub.label}</span>
        {sub.estimated_tokens != null && (
          <ChunkSizeBadge
            estimatedTokens={sub.estimated_tokens}
            isChunk={true}
            {...(wasEstimatedTokens !== undefined
              ? { wasEstimatedTokens }
              : {})}
            t={t}
          />
        )}
      </div>
      <p className={styles.secCollapsed}>… {t(key)} …</p>
    </div>
  );
}

// ─── Selection toolbar (MR 7) ─────────────────────────────────────────

/** Sticky dark toolbar that appears below the reading header when the
 *  reviewer has selected text inside the current section. Split is the
 *  primary action; Move-to-prev / Move-to-next are the boundary
 *  adjustments; Rewrite / Insert / Exclude write paragraph_overrides
 *  into the current sub. Rewrite + Insert open an inline paragraph
 *  editor at the selected paragraph slot (the parent manages the
 *  editor state via `onOpenRewrite` / `onOpenInsert`). */
function SelectionToolbar({
  selection,
  canMovePrev,
  canMoveNext,
  prevRef,
  nextRef,
  onAction,
  onOpenRewrite,
  onOpenInsert,
  t,
}: {
  selection: { startParaIdx: number; endParaIdx: number; wordCount: number };
  canMovePrev: boolean;
  canMoveNext: boolean;
  prevRef: string | null;
  nextRef: string | null;
  onAction: (action: SelectionAction) => void;
  onOpenRewrite: (paragraphIdx: number) => void;
  onOpenInsert: (paragraphIdx: number) => void;
  t: (k: string) => string;
}) {
  const paraCount = selection.endParaIdx - selection.startParaIdx + 1;
  const meta = t('mapReview.selectionCountTemplate')
    .replace('{p}', String(paraCount))
    .replace('{w}', String(selection.wordCount));
  return (
    <div
      className={styles.selectionToolbar}
      role="toolbar"
      aria-label={t('mapReview.selectionToolbarAria')}
    >
      <span className={styles.selectionMeta}>{meta}</span>
      {/* Split moved out of this toolbar — it's a section-tier action
        * now, triggered from the SectionFloatingMenu with a dedicated
        * drag bar. The old selection-based Split hijacked the browser's
        * text-selection and conflicted with normal reading. */}
      {canMovePrev && prevRef && (
        <button
          type="button"
          className={styles.selBtn}
          onClick={() =>
            onAction({
              kind: 'move-to-prev',
              startParaIdx: selection.startParaIdx,
              endParaIdx: selection.endParaIdx,
            })
          }
        >
          {t('mapReview.selectionMoveToTemplate').replace('{ref}', prevRef)}
          <kbd className={styles.selKbd}>⇧←</kbd>
        </button>
      )}
      {canMoveNext && nextRef && (
        <button
          type="button"
          className={styles.selBtn}
          onClick={() =>
            onAction({
              kind: 'move-to-next',
              startParaIdx: selection.startParaIdx,
              endParaIdx: selection.endParaIdx,
            })
          }
        >
          {t('mapReview.selectionMoveToTemplate').replace('{ref}', nextRef)}
          <kbd className={styles.selKbd}>⇧→</kbd>
        </button>
      )}
      {/* MR 8 — paragraph-content actions, enabled by MR 7b's data
        * model. Rewrite + Insert open an inline textarea at the
        * target paragraph; Exclude writes the override directly. */}
      <button
        type="button"
        className={styles.selBtn}
        onClick={() => onOpenRewrite(selection.startParaIdx)}
      >
        {t('mapReview.selectionRewrite')}
        <kbd className={styles.selKbd}>E</kbd>
      </button>
      <button
        type="button"
        className={styles.selBtn}
        onClick={() => onOpenInsert(selection.endParaIdx)}
      >
        {t('mapReview.selectionInsertAfter')}
        <kbd className={styles.selKbd}>I</kbd>
      </button>
      <button
        type="button"
        className={styles.selBtnDanger}
        onClick={() =>
          onAction({
            kind: 'exclude-paragraph',
            paragraphIdx: selection.startParaIdx,
          })
        }
      >
        {t('mapReview.selectionExclude')}
        <kbd className={styles.selKbd}>X</kbd>
      </button>
    </div>
  );
}

// ─── Section floating menu (section-tier actions on the right pane) ──
//
// Replaces the left-pane row cluster for section-tier actions. Lives on
// the current section's header in the right pane so edits happen where
// the reviewer's eye already is. Three verbs: Split, Merge up (absorb
// this section into the one above), Delete.
//
// The menu is hidden entirely during split mode — the sticky result
// card + banner carry the Cancel · Esc control, so there's no second
// toggle to confuse the reviewer.

function SectionFloatingMenu({
  canMergeUp,
  onSplit,
  onMergeUp,
  onDelete,
  t,
}: {
  canMergeUp: boolean;
  onSplit: () => void;
  onMergeUp: () => void;
  onDelete: () => void;
  t: (k: string) => string;
}) {
  return (
    <div
      className={styles.sectionMenu}
      role="toolbar"
      aria-label={t('mapReview.sectionMenuAria')}
    >
      <button
        type="button"
        className={styles.sectionMenuBtn}
        onClick={onSplit}
      >
        {t('mapReview.sectionMenuSplit')}
      </button>
      {canMergeUp && (
        <button
          type="button"
          className={styles.sectionMenuBtn}
          onClick={onMergeUp}
        >
          {t('mapReview.sectionMenuMergeUp')}
        </button>
      )}
      <button
        type="button"
        className={joinSecClasses(styles.sectionMenuBtn, styles.sectionMenuBtnDanger)}
        onClick={onDelete}
      >
        {t('mapReview.sectionMenuDelete')}
      </button>
    </div>
  );
}

// ─── Split redesign (2026-10-26 handoff) ─────────────────────────────
//
// The designer handoff `for-engineering-split-new` replaces the horizontal
// bars + caret-at-point caret of the 2026-10-22 model with a word-level
// interaction:
//
//   1. SPLIT → section enters an `armed` state (brand frame + Cancel
//      banner). A SectionFloatingMenu Split button triggers it.
//   2. Hover inside the section body → a blinking caret snaps between
//      words; everything after the caret tints violet.
//   3. Click → the caret becomes a solid cut with a draggable grip, and
//      a sticky result card at the top shows the two halves with an
//      editable title + the derived anchor phrase.
//   4. "+ End it further down" asks for a second click below the first
//      cut → carve, three sections, each half gets its own title/anchor.
//   5. Create section ↵ commits via onSelectionAction({kind:'split-range'}).
//      Esc cancels.
//
// Pattern derivation: `derivePattern` tries 5-word prefixes starting at
// the cut word, extending to 14 words until the prefix is unique in the
// chapter body. Live preview only — the server-side chunker is the
// authoritative anchor-matcher at save time.

/** Count whole-string occurrences of `needle` in `haystack`. Linear-time
 *  via indexOf hopping; good enough for a chapter body of ~10k chars. */
function countOccurrences(haystack: string, needle: string): number {
  if (!needle || needle.length > haystack.length) return 0;
  let count = 0;
  let idx = 0;
  while ((idx = haystack.indexOf(needle, idx)) !== -1) {
    count++;
    idx += needle.length;
  }
  return count;
}

interface PatternResult {
  /** Number of words in the anchor phrase (5 → 14). */
  words: number;
  /** The literal text of the anchor. */
  text: string;
  /** True when the anchor had to extend past the default 5 words to
   *  become unique. Shows the EXTENDED TO N WORDS hint on the card. */
  ext: boolean;
  /** True when even 14 words didn't produce a unique anchor — the
   *  anchor will still work most of the time (the chunker uses a
   *  longer slice than this preview), but we warn the reviewer. */
  fail?: boolean;
}

function derivePattern(
  wordIdx: number,
  words: readonly string[],
  chapterBodyText: string,
): PatternResult | null {
  if (wordIdx < 0 || wordIdx >= words.length) return null;
  for (let n = 5; n <= 14 && wordIdx + n <= words.length; n++) {
    const text = words.slice(wordIdx, wordIdx + n).join(' ');
    if (countOccurrences(chapterBodyText, text) === 1) {
      return { words: n, text, ext: n > 5 };
    }
  }
  const fallback = words.slice(wordIdx, wordIdx + Math.min(5, words.length - wordIdx)).join(' ');
  return { words: Math.min(5, words.length - wordIdx), text: fallback, ext: false, fail: true };
}

/** Dark banner shown at the top of the section while in the `armed`
 *  state (before a cut is placed). Instruction + Cancel · Esc. */
function SplitBanner({ t, onCancel }: { t: (k: string) => string; onCancel: () => void }) {
  return (
    <div className={styles.splitBanner} role="status" aria-live="polite">
      <span className={styles.splitBannerIcon} aria-hidden="true">✂</span>
      <span className={styles.splitBannerText}>{t('mapReview.splitBanner')}</span>
      <button
        type="button"
        className={styles.splitBannerCancel}
        onClick={onCancel}
      >
        {t('mapReview.splitCancel')}
      </button>
    </div>
  );
}

/** Word-indexed body renderer. Each paragraph becomes a `<p>` holding
 *  `<span class="w" data-i="N">word</span>` children; the renderer
 *  decorates each span with the hover / cut / preview-tint / anchor
 *  classes based on the current split state. Hover + click + drag are
 *  all driven by mouse-event delegation at the paragraph level — one
 *  listener per paragraph instead of one per word. */
function SplitBody({
  paragraphs,
  wordIndex,
  state,
  hover,
  drag,
  chapterBodyText,
  onHoverChange,
  onClickBoundary,
  onDragStart,
  onDragMove,
  onDragEnd,
  t,
}: {
  paragraphs: readonly string[];
  wordIndex: {
    words: string[];
    paraOfWord: number[];
    charOffsetOfWord: number[];
  };
  state: Exclude<SplitState, null>;
  hover: number | null;
  drag: 'a' | 'b' | null;
  chapterBodyText: string;
  onHoverChange: (i: number | null) => void;
  onClickBoundary: (boundary: number) => void;
  onDragStart: (which: 'a' | 'b') => void;
  onDragMove: (which: 'a' | 'b', boundary: number) => void;
  onDragEnd: () => void;
  t: (k: string) => string;
}) {
  const { words, paraOfWord } = wordIndex;

  // Resolve the cuts out of the state. carveArm means we're between
  // clicks for a 3-way split; the caret should track the second cut
  // position but the first cut is already placed.
  const cutA = state.stage === 'oneCut' || state.stage === 'twoCuts' ? state.a : null;
  const cutB = state.stage === 'twoCuts' ? state.b : null;
  const carveArming = state.stage === 'oneCut' && state.carveArm;
  const armedForFirst = state.stage === 'armed';

  // Which cut (if any) is currently hovered — drives the preview tint
  // on words AFTER the hover position while there is no placed cut yet.
  // Once cutA is placed, the "preview after" region is anchored to it,
  // not to hover.
  const previewCut = cutA ?? (armedForFirst ? hover : null);

  // Pattern slices: pat for cut A (or hover-preview), pat2 for cut B.
  const patA =
    previewCut != null && state.stage !== 'armed'
      ? derivePattern(previewCut, words, chapterBodyText)
      : null;
  const patB = cutB != null ? derivePattern(cutB, words, chapterBodyText) : null;

  // Global mousemove/mouseup handlers for grip drag. Scoped to drag !==
  // null so we don't attach them in the common case.
  useEffect(() => {
    if (!drag) return;
    const onMove = (e: MouseEvent) => {
      const boundary = boundaryFromEvent(e, words.length);
      if (boundary == null) return;
      onDragMove(drag, boundary);
    };
    const onUp = () => onDragEnd();
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [drag, onDragMove, onDragEnd, words.length]);

  // Delegated mousemove per paragraph — one listener instead of one per
  // word span. Returns the boundary (word index BEFORE which the caret
  // sits, 0 → len).
  const onParaMove = useCallback(
    (e: React.MouseEvent<HTMLParagraphElement>) => {
      if (drag) return; // drag has its own handler
      if (!(armedForFirst || carveArming)) return;
      const boundary = boundaryFromEvent(e.nativeEvent, words.length);
      if (boundary == null) return;
      // Carve guard: the second cut can't land within 3 words of the first.
      if (carveArming && cutA != null && boundary <= cutA + 3) return;
      if (boundary !== hover) onHoverChange(boundary);
    },
    [drag, armedForFirst, carveArming, cutA, hover, onHoverChange, words.length],
  );

  const onParaClick = useCallback(
    (e: React.MouseEvent<HTMLParagraphElement>) => {
      if (drag) return;
      if (!(armedForFirst || carveArming)) return;
      // Ignore clicks that originated on a grip — grips are drag
      // handles, not cut-placement sites. Without this a stationary
      // click on a grip would place a redundant cut at its own
      // position.
      const target = e.target as Element | null;
      if (target && target.closest(`.${styles['wGrip']}`)) return;
      const boundary = boundaryFromEvent(e.nativeEvent, words.length);
      if (boundary == null) return;
      onClickBoundary(boundary);
    },
    [drag, armedForFirst, carveArming, onClickBoundary, words.length],
  );

  return (
    <>
      {paragraphs.map((_p, pi) => {
        // Range of word indices that live in this paragraph.
        const firstWord = paraOfWord.indexOf(pi);
        if (firstWord === -1) return null;
        let lastWord = firstWord;
        while (lastWord + 1 < words.length && paraOfWord[lastWord + 1] === pi) {
          lastWord++;
        }
        const spans: ReactNode[] = [];
        for (let i = firstWord; i <= lastWord; i++) {
          // `styles.X` resolves through the CSS-module index signature,
          // which gives us `string | undefined` under
          // noUncheckedIndexedAccess — the fallback empty strings are
          // filtered out at .join time.
          const cls: (string | undefined)[] = [styles['w']];
          const isCutA = cutA === i;
          const isCutB = cutB === i;
          const isHoverCaret =
            !cutA && armedForFirst && hover === i
              ? true
              : carveArming && hover === i
                ? true
                : false;
          // Preview tint: words from the cut (or hover) onwards get `.wNb`;
          // if we have a cutB, words at/after cutB get `.wRs` instead.
          if (previewCut != null && i >= previewCut && (cutB == null || i < cutB)) {
            cls.push(styles['wNb']);
          }
          if (cutB != null && i >= cutB) {
            cls.push(styles['wRs']);
          }
          // Anchor underline for the derived pattern window.
          if (patA && previewCut != null && i >= previewCut && i < previewCut + patA.words) {
            cls.push(styles['wPat']);
          }
          if (patB && cutB != null && i >= cutB && i < cutB + patB.words) {
            cls.push(styles['wPat']);
          }
          if (isCutA) cls.push(styles['wCt']);
          if (isCutB) cls.push(styles['wCt2']);
          if (isHoverCaret) cls.push(styles['wHv']);
          const children: ReactNode[] = [words[i] ?? ''];
          if (isCutA || isHoverCaret) {
            // Hint tooltip above the hover caret, or grip above the
            // placed first cut.
            if (isHoverCaret) {
              const prevWord = words[i - 1] ?? '';
              const sentenceStart = /[.:;!?»)]$/.test(prevWord);
              children.push(
                <span key="hint" className={styles.wHint}>
                  {sentenceStart
                    ? t('mapReview.splitHintSentence')
                    : t('mapReview.splitHintCut')}
                </span>,
              );
            } else if (isCutA && state.stage !== 'armed') {
              children.push(
                <span
                  key="gripA"
                  className={styles.wGrip}
                  onMouseDown={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    onDragStart('a');
                  }}
                >
                  {t('mapReview.splitGripNew')}
                </span>,
              );
            }
          }
          if (isCutB) {
            children.push(
              <span
                key="gripB"
                className={joinSecClasses(styles.wGrip, styles.wGripB)}
                onMouseDown={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  onDragStart('b');
                }}
              >
                {t('mapReview.splitGripResume').replace('{parent}', '')}
              </span>,
            );
          }
          spans.push(
            <span key={i} className={cls.filter(Boolean).join(' ')} data-i={i}>
              {children}
            </span>,
          );
          // Keep spacing between words.
          if (i < lastWord) spans.push(<span key={`sp-${i}`}> </span>);
        }
        return (
          <p
            key={pi}
            data-para-idx={pi}
            className={styles.splitPara}
            onMouseMove={onParaMove}
            onClick={onParaClick}
          >
            {spans}
          </p>
        );
      })}
    </>
  );
}

/** Resolve a mouse event to a word boundary index (0..words.length).
 *  The boundary is "insert the caret BEFORE the word at this index",
 *  with left-half → this word, right-half → next word. Returns null
 *  when the event's target isn't inside a word span. */
function boundaryFromEvent(e: MouseEvent, maxWords: number): number | null {
  const target = e.target as Element | null;
  if (!target) return null;
  const span = target.closest('[data-i]') as HTMLElement | null;
  if (!span) return null;
  const i = parseInt(span.dataset['i'] ?? '', 10);
  if (!Number.isFinite(i)) return null;
  const r = span.getBoundingClientRect();
  return e.clientX < r.left + r.width / 2 ? i : Math.min(i + 1, maxWords - 1);
}

/** Sticky result card — the preview of the resulting sections while
 *  split mode is active. Shows up to three "halves" (parent-keeps / new
 *  / resumes), each with an editable title + token badge; a dedicated
 *  row for each derived anchor phrase; and the primary / secondary /
 *  cancel action buttons. */
function SplitResultCard({
  state,
  words,
  chapterBodyText,
  parentRef,
  onChangeTitleA,
  onChangeTitleB,
  onArmCarve,
  onRemoveEnd,
  onCommit,
  onCancel,
  t,
}: {
  state: Exclude<SplitState, null | { stage: 'armed' }>;
  words: readonly string[];
  chapterBodyText: string;
  parentRef: string;
  onChangeTitleA: (title: string) => void;
  onChangeTitleB: (title: string) => void;
  onArmCarve: () => void;
  onRemoveEnd: () => void;
  onCommit: () => void;
  onCancel: () => void;
  t: (k: string) => string;
}) {
  const carve = state.stage === 'twoCuts';
  const patA = derivePattern(state.a, words, chapterBodyText);
  const patB = carve ? derivePattern(state.b, words, chapterBodyText) : null;
  const firstWordsA = words.slice(state.a, state.a + 5).join(' ');
  const firstWordsB = carve ? words.slice(state.b, state.b + 3).join(' ') : '';
  const titleA = state.titleA;
  const titleB = carve ? state.titleB : '';
  // Default title placeholder = first words of the slice. The reviewer
  // can type their own; we only commit the typed title (non-empty).
  const titlePlaceholderA = `${firstWordsA}…`;
  const titlePlaceholderB = t('mapReview.splitResumesTitle')
    .replace('{parent}', parentRef)
    .replace('{firstWords}', firstWordsB);
  return (
    <div className={styles.splitCard}>
      <div className={styles.splitCardHead}>
        <span className={styles.splitCardHeadIcon} aria-hidden="true">✂</span>
        <span>
          {carve ? t('mapReview.splitCardCarve') : t('mapReview.splitCardSplit')}
        </span>
        <span className={styles.splitCardSpacer} />
        <span className={styles.splitCardHint}>{t('mapReview.splitCardDrag')}</span>
      </div>
      <div
        className={joinSecClasses(styles.splitHalves, carve && styles.splitHalvesThree)}
      >
        <div className={styles.splitHalf}>
          <div className={styles.splitHalfKicker}>
            <span className={joinSecClasses(styles.splitHalfSwatch, styles.splitHalfSwatchKeeps)} />
            {t('mapReview.splitKeeps')}
          </div>
          <div className={styles.splitHalfNm}>{parentRef}</div>
        </div>
        <div className={styles.splitHalf}>
          <div className={styles.splitHalfKicker}>
            <span className={joinSecClasses(styles.splitHalfSwatch, styles.splitHalfSwatchNew)} />
            {t('mapReview.splitNew')}
          </div>
          <input
            className={styles.splitHalfInput}
            value={titleA}
            placeholder={titlePlaceholderA}
            onChange={(e) => onChangeTitleA(e.target.value)}
            aria-label={t('mapReview.splitNew')}
          />
        </div>
        {carve && (
          <div className={styles.splitHalf}>
            <div className={styles.splitHalfKicker}>
              <span className={joinSecClasses(styles.splitHalfSwatch, styles.splitHalfSwatchResume)} />
              {t('mapReview.splitResumes').replace('{parent}', parentRef)}
            </div>
            <input
              className={styles.splitHalfInput}
              value={titleB}
              placeholder={titlePlaceholderB}
              onChange={(e) => onChangeTitleB(e.target.value)}
              aria-label={t('mapReview.splitResumes').replace('{parent}', parentRef)}
            />
          </div>
        )}
      </div>
      {patA && (
        <div className={styles.splitAnchor}>
          <span className={styles.splitAnchorKicker}>{t('mapReview.splitStartsAt')}</span>
          <span className={styles.splitAnchorQuote}>« {patA.text} »</span>
          <span
            className={
              patA.ext ? styles.splitAnchorWarn : styles.splitAnchorOk
            }
          >
            {patA.ext
              ? t('mapReview.splitExtended').replace('{n}', String(patA.words))
              : t('mapReview.splitUnique')}
          </span>
        </div>
      )}
      {patA?.ext && (
        <div className={styles.splitAnchorBody}>
          <span className={styles.splitAnchorBodyIcon} aria-hidden="true">◷</span>
          <span>{t('mapReview.splitExtendedBody')}</span>
        </div>
      )}
      {patB && (
        <div className={styles.splitAnchor}>
          <span className={styles.splitAnchorKicker}>
            {t('mapReview.splitResumesAt').replace('{parent}', parentRef)}
          </span>
          <span className={styles.splitAnchorQuote}>« {patB.text} »</span>
          <span
            className={
              patB.ext ? styles.splitAnchorWarn : styles.splitAnchorOk
            }
          >
            {patB.ext
              ? t('mapReview.splitExtended').replace('{n}', String(patB.words))
              : t('mapReview.splitUnique')}
          </span>
        </div>
      )}
      {state.stage === 'oneCut' && state.carveArm && (
        <div className={styles.splitCarveArm}>
          <span className={styles.splitCarveArmIcon} aria-hidden="true">↓</span>
          <span>
            {t('mapReview.splitEndFurtherPrompt').replace('{parent}', parentRef)}
          </span>
        </div>
      )}
      <div className={styles.splitCardFoot}>
        <button
          type="button"
          className={styles.splitCardCommit}
          onClick={onCommit}
        >
          {carve
            ? t('mapReview.splitCreateCarve')
            : t('mapReview.splitCreate')}
          <kbd className={styles.splitCardKbd}>↵</kbd>
        </button>
        {!carve ? (
          <button
            type="button"
            className={styles.splitCardAlt}
            onClick={onArmCarve}
          >
            {t('mapReview.splitEndFurther')}
          </button>
        ) : (
          <button
            type="button"
            className={styles.splitCardAlt}
            onClick={onRemoveEnd}
          >
            {t('mapReview.splitRemoveEnd')}
          </button>
        )}
        <span className={styles.splitCardSpacer} />
        <button
          type="button"
          className={styles.splitCardCancel}
          onClick={onCancel}
        >
          {t('mapReview.splitCancel')}
        </button>
      </div>
    </div>
  );
}

// ─── Selection helpers ────────────────────────────────────────────────

/** Walk up from a DOM node to the nearest ancestor carrying a
 *  `data-para-idx` attribute — that's the paragraph the selection
 *  endpoint sits in. Returns the parsed index, or null when the walk
 *  reached the document root without hitting one (selection endpoint
 *  is outside any paragraph — e.g. on the section header or between
 *  sections). */
function findParaIdx(node: Node): number | null {
  let el: Node | null = node;
  while (el && el.nodeType !== Node.DOCUMENT_NODE) {
    if (el instanceof HTMLElement && el.dataset['paraIdx'] !== undefined) {
      const parsed = parseInt(el.dataset['paraIdx'] as string, 10);
      return Number.isFinite(parsed) ? parsed : null;
    }
    el = el.parentNode;
  }
  return null;
}

// ─── Tier-tinted current-section background ──────────────────────────

/** Pick the tier-tinted fill class for the current section based on
 *  the sub's estimated_tokens. Returns null for tiny / null tiers so
 *  the .secCurrent brand-soft fallback stays in place — no strong
 *  actionable signal to override with. Same shape as MapTree's
 *  focusedTierClass so the two panes read as one system. */
function tierFillClass(
  s: { readonly [k: string]: string },
  estimatedTokens: number | null | undefined,
): string | null {
  if (estimatedTokens == null) return null;
  const tier = classifyChunkSize(estimatedTokens);
  if (tier === 'good') return s['secCurrentGood'] ?? null;
  if (tier === 'large') return s['secCurrentLarge'] ?? null;
  if (tier === 'oversized') return s['secCurrentOversized'] ?? null;
  return null;
}

function joinSecClasses(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

/** Pick the `<p>` class based on which paragraph override (if any) is
 *  active on this slot. The two tints are permanent — the brief wants
 *  provenance visible on a re-read, not only during the edit. */
function paragraphClass(
  s: { readonly [k: string]: string },
  flags: { excluded: boolean; rewritten: boolean },
): string | undefined {
  if (flags.excluded) return s['paraExcluded'];
  if (flags.rewritten) return s['paraRewritten'];
  return undefined;
}

// ─── Boundary drag (MR 8) ─────────────────────────────────────────────
//
// The 1px rule between the current section and its neighbour becomes a
// drag affordance. The reviewer grabs the rule, moves vertically; we
// highlight the paragraph boundary nearest to the pointer, and on
// release fire the matching Move action.
//
// Semantics:
//   - TOP boundary (prev → current): dragging DOWN moves the first N
//     paragraphs of the current section UP into the prev section (same
//     wire shape as the Range toolbar's "Move to §prev"). Dragging up
//     is a no-op in V1 — the prev section's body isn't visible, so we
//     can't let the reviewer pull paragraphs INTO the current section
//     from above without rendering their content.
//   - BOTTOM boundary (current → next): dragging UP moves the last N
//     paragraphs of the current section DOWN into the next section.
//
// Implementation:
//   - pointerdown captures the pointer, puts us in "dragging" mode
//   - pointermove walks the body's `[data-para-idx]` elements and
//     picks the one whose boundary (top edge for TOP drag, bottom edge
//     for BOTTOM drag) is nearest to the pointer; we highlight that
//     boundary with a 2px brand line via a `data-snap-idx` attribute
//     the CSS keys off
//   - pointerup fires the Move action and clears the drag state
//
// If the drag doesn't cross at least one paragraph boundary, we treat
// it as a no-op click (preserves the hover-only "Drag to move" label
// without forcing the reviewer to drag precisely).

interface BoundaryDragProps {
  edge: 'top' | 'bottom';
  bodyRef: React.RefObject<HTMLDivElement | null>;
  paragraphCount: number;
  onCommit: (snapIdx: number) => void;
  t: (k: string) => string;
}

function BoundaryDrag(p: BoundaryDragProps) {
  const [snapIdx, setSnapIdx] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);

  const computeSnap = useCallback(
    (clientY: number): number | null => {
      const body = p.bodyRef.current;
      if (!body) return null;
      const paras = Array.from(body.querySelectorAll<HTMLElement>('[data-para-idx]'));
      if (paras.length === 0) return null;
      // Choose the paragraph whose relevant edge (top for TOP drag,
      // bottom for BOTTOM drag) is closest to the pointer. We return
      // the paragraph INDEX; the parent interprets it per edge.
      let best = -1;
      let bestDist = Infinity;
      for (const el of paras) {
        const rect = el.getBoundingClientRect();
        const edgeY = p.edge === 'top' ? rect.top : rect.bottom;
        const dist = Math.abs(clientY - edgeY);
        if (dist < bestDist) {
          bestDist = dist;
          const idx = parseInt(el.dataset['paraIdx'] ?? '', 10);
          if (Number.isFinite(idx)) best = idx;
        }
      }
      return best >= 0 ? best : null;
    },
    [p.bodyRef, p.edge],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (p.paragraphCount < 2) return;
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      setDragging(true);
      setSnapIdx(computeSnap(e.clientY));
    },
    [computeSnap, p.paragraphCount],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging) return;
      setSnapIdx(computeSnap(e.clientY));
    },
    [dragging, computeSnap],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      setDragging(false);
      if (snapIdx !== null) p.onCommit(snapIdx);
      setSnapIdx(null);
    },
    [snapIdx, p],
  );

  return (
    <div
      className={joinSecClasses(
        styles.bnd,
        dragging && styles.bndDragging,
      )}
      role="separator"
      aria-label={p.t('mapReview.boundaryDragLabel')}
      data-l={p.t('mapReview.boundaryDragLabel')}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    />
  );
}
